import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Convert ISO timestamp to IST date string (YYYY-MM-DD)
function toISTDate(isoStr) {
  if (!isoStr) return isoStr;
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  } catch { return isoStr; }
}

// Normalize FY dates to clean IST date strings
function normalizeFY(year) {
  if (!year) return year;
  return { ...year, start_date: toISTDate(year.start_date), end_date: toISTDate(year.end_date) };
}

const FinancialYearContext = createContext({
  financialYears: [],
  selectedYear: null,
  setSelectedYear: () => {},
  loading: true,
  refreshYears: () => {},
});

export const useFinancialYear = () => {
  const context = useContext(FinancialYearContext);
  if (!context) {
    throw new Error("useFinancialYear must be used within a FinancialYearProvider");
  }
  return context;
};

export const FinancialYearProvider = ({ children }) => {
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYearState] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch financial years from API
  const fetchFinancialYears = async () => {
    try {
      const token = localStorage.getItem("foundry-token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/accounting/financial-years`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const years = response.data || [];
      setFinancialYears(years);

      // Get stored year from localStorage or use current year
      const storedYearId = localStorage.getItem("foundry-financial-year");

      if (storedYearId) {
        const storedYear = years.find(y => y.id === storedYearId);
        if (storedYear) {
          setSelectedYearState(normalizeFY(storedYear));
        } else {
          const currentYear = years.find(y => y.is_current);
          if (currentYear) {
            setSelectedYearState(normalizeFY(currentYear));
            localStorage.setItem("foundry-financial-year", currentYear.id);
          } else if (years.length > 0) {
            setSelectedYearState(normalizeFY(years[0]));
            localStorage.setItem("foundry-financial-year", years[0].id);
          }
        }
      } else {
        const currentYear = years.find(y => y.is_current);
        if (currentYear) {
          setSelectedYearState(normalizeFY(currentYear));
          localStorage.setItem("foundry-financial-year", currentYear.id);
        } else if (years.length > 0) {
          setSelectedYearState(normalizeFY(years[0]));
          localStorage.setItem("foundry-financial-year", years[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch financial years:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  // Set selected year and save to localStorage
  const setSelectedYear = (year) => {
    setSelectedYearState(normalizeFY(year));
    if (year) {
      localStorage.setItem("foundry-financial-year", year.id);
    } else {
      localStorage.removeItem("foundry-financial-year");
    }
  };

  const refreshYears = () => {
    fetchFinancialYears();
  };

  return (
    <FinancialYearContext.Provider
      value={{
        financialYears,
        selectedYear,
        setSelectedYear,
        loading,
        refreshYears,
      }}
    >
      {children}
    </FinancialYearContext.Provider>
  );
};
